/** @jsxImportSource react */
import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useThemeLanguage } from '@/contexts/ThemeLanguageContext';
import './AppModal.css';

export type AppModalProps = {
  open: boolean;
  onClose: () => void;
  title: string;
  /** Message or content (ReactNode supported) */
  children: React.ReactNode;
  /** Optional custom icon (e.g. logout). If not set, shows default trash icon with danger style */
  icon?: React.ReactNode;
  /** If false, hide the icon (e.g. for save/success modals). Default true */
  showIcon?: boolean;
  /** Optional warning line below message */
  warning?: string;
  onConfirm?: () => void;
  confirmLabel?: string;
  confirmVariant?: 'danger' | 'primary';
  confirmLoading?: boolean;
  /** Show cancel button (default true when onConfirm is set) */
  showCancel?: boolean;
  cancelLabel?: string;
};

const defaultTrashIcon = React.createElement(
  'svg',
  { viewBox: '0 0 24 24', fill: 'none', xmlns: 'http://www.w3.org/2000/svg', width: 24, height: 24 },
  React.createElement('path', { d: 'M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2', stroke: 'currentColor', strokeWidth: '2', strokeLinecap: 'round' }),
  React.createElement('path', { d: 'M10 11v6M14 11v6', stroke: 'currentColor', strokeWidth: '2', strokeLinecap: 'round' })
);

export function AppModal({
  open,
  onClose,
  title,
  children,
  icon: customIcon,
  showIcon = true,
  warning,
  onConfirm,
  confirmLabel,
  confirmVariant = 'primary',
  confirmLoading = false,
  showCancel = true,
  cancelLabel,
}: AppModalProps) {
  const { t } = useThemeLanguage();

  useEffect(() => {
    if (!open) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  const modalContent = React.createElement(
    'div',
    { className: 'app-modal-overlay', onClick: handleOverlayClick, role: 'dialog', 'aria-modal': true, 'aria-labelledby': 'app-modal-title' },
    React.createElement(
      'div',
      { className: 'app-modal-content' },
      React.createElement(
        'div',
        { className: 'app-modal-header' },
        showIcon !== false && React.createElement('span', { className: customIcon ? 'app-modal-icon' : 'app-modal-icon app-modal-icon--danger' }, customIcon ?? defaultTrashIcon),
        React.createElement('h3', { id: 'app-modal-title', className: 'app-modal-title' }, title)
      ),
      React.createElement('div', { className: 'app-modal-body' },
        typeof children === 'string' ? React.createElement('p', { className: 'app-modal-message' }, children) : children,
        warning ? React.createElement('p', { className: 'app-modal-warning' }, warning) : null
      ),
      (onConfirm || showCancel) && React.createElement(
        'div',
        { className: 'app-modal-actions' },
        showCancel && React.createElement(
          'button',
          { type: 'button', onClick: onClose, className: 'app-modal-btn app-modal-btn--cancel' },
          cancelLabel ?? t('modal.cancel')
        ),
        onConfirm && React.createElement(
          'button',
          {
            type: 'button',
            onClick: onConfirm,
            disabled: confirmLoading,
            className: `app-modal-btn app-modal-btn--confirm app-modal-btn--${confirmVariant}`,
          },
          confirmLoading ? React.createElement('span', { className: 'app-modal-spinner' }) : (confirmLabel ?? t('modal.delete'))
        )
      )
    )
  );

  return ReactDOM.createPortal(modalContent, document.body);
}
